// A job's purchase order as a file (round 45, Nathan: "add a file upload
// option here (ensuring it is both drag and drop and browse) for
// word/pdf/image and make sure it views ok").
//
// It sits beside the PO number. Dropping or choosing a file uploads it at
// once (nothing waits on the form's Save), and a new one REPLACES the last on
// the server, which keeps the old one in Recently deleted. A PDF or an image
// opens in the app's viewer (never a bare link: a file never strands a Home
// Screen app); a Word document downloads through DownloadLink.
//
// Managers only. The server refuses the file to a field worker however they
// ask (attachments/access.ts), because a purchase order usually states the
// price.
import { useState } from 'react';
import { ApiError } from '../../api';
import { documents, uploadAttachment, type Attachment } from '../../documentsApi';
import { FileDrop } from '../ui/FileDrop';
import { DocumentViewer } from '../DocumentViewer';
import { useConfirm } from '../ui/confirm';

export const PO_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif';

export function PurchaseOrderFile({
  jobId,
  file,
  onChanged,
  compact = false,
  attachLabel = 'Attach the PO',
}: {
  jobId: string;
  file: Attachment | null;
  onChanged: (file: Attachment | null) => void;
  /** The view mode's one line, rather than the edit form's drop zone. */
  compact?: boolean;
  /** The compact button's words when there is no file yet. */
  attachLabel?: string;
}) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState(false);

  async function send(chosen: File) {
    setBusy(true);
    setError(null);
    try {
      const { attachment } = await uploadAttachment({ kind: 'job', ownerId: jobId, file: chosen, purchaseOrder: true });
      onChanged(attachment);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not upload that file.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!file) return;
    const ok = await confirm({
      title: 'Remove the purchase order file?',
      message: 'The PO number stays. An admin can get the file back for 30 days.',
      confirmLabel: 'Remove',
      tone: 'warning',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await documents.remove(file.id);
      onChanged(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove that.');
    } finally {
      setBusy(false);
    }
  }

  const picker = (
    <FileDrop
      variant={compact ? 'button' : 'zone'}
      accept={PO_ACCEPT}
      typeLabel="PDF, Word or an image"
      disabled={busy}
      buttonClassName="row-action"
      label={
        busy ? 'Uploading…' : compact ? (file ? 'Replace' : attachLabel) : file ? 'Drop or choose a new file to replace it' : undefined
      }
      hint={compact ? undefined : 'PDF, Word or an image.'}
      onFiles={(files) => files[0] && void send(files[0])}
      onReject={setError}
    />
  );

  return (
    <div className="min-w-0">
      {file && (
        <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          <button
            type="button"
            className="table-link max-w-full truncate text-left"
            onClick={() => setViewing(true)}
            title={file.title}
          >
            {file.title}
          </button>
          <button type="button" className="row-action-muted" disabled={busy} onClick={() => void remove()}>
            Remove
          </button>
          {compact && picker}
        </div>
      )}
      {(!file || !compact) && picker}
      {error && (
        <p className="field-error-inline mt-1" role="alert">
          {error}
        </p>
      )}
      {viewing && file && <DocumentViewer attachment={file} onClose={() => setViewing(false)} />}
    </div>
  );
}
